import React, { useEffect, useState } from "react";
import api, { API_BASE } from "@/lib/api";
import { useEngagement } from "@/contexts/EngagementContext";
import PageHeader from "@/components/PageHeader";
import TButton from "@/components/TButton";
import { Briefcase, Plus, Loader2, Archive, FileText, Radio, Image as ImageIcon } from "lucide-react";
import { toast } from "sonner";

const Engagements = () => {
  const { engagements, refresh, setActiveId, activeId } = useEngagement();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [draft, setDraft] = useState({
    name: "", client_name: "", engagement_type: "project",
    scope_domains: "", scope_ip_ranges: "", notes: "",
  });

  useEffect(() => { refresh(); }, [refresh]);

  const create = async () => {
    if (!draft.name.trim() || !draft.client_name.trim()) {
      toast.error("Name and Client Name are required");
      return;
    }
    setBusy(true);
    try {
      const payload = {
        ...draft,
        scope_domains: draft.scope_domains.split(",").map((s) => s.trim()).filter(Boolean),
        scope_ip_ranges: draft.scope_ip_ranges.split(",").map((s) => s.trim()).filter(Boolean),
      };
      const { data } = await api.post("/engagements", payload);
      toast.success(`Engagement "${data.name}" created`);
      setOpen(false);
      setDraft({ name: "", client_name: "", engagement_type: "project", scope_domains: "", scope_ip_ranges: "", notes: "" });
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally {
      setBusy(false);
    }
  };

  const archive = async (id) => {
    if (!window.confirm("Archive this engagement? All its data will be hidden.")) return;
    try {
      await api.delete(`/engagements/${id}`);
      toast.success("Archived");
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  const downloadScopeLetter = async (id) => {
    try {
      const res = await api.get(`/engagements/${id}/scope-letter`, { responseType: "blob" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      a.download = `scope-letter-${id.slice(0, 8)}.pdf`;
      a.click();
      toast.success("Scope letter generated");
    } catch (e) {
      toast.error("Failed to generate letter");
    }
  };

  const registerAgent = async (engId) => {
    const name = window.prompt("Name this collector agent (e.g., 'Acme Jump Host'):");
    if (!name) return;
    try {
      const { data } = await api.post(`/engagements/${engId}/agents`, { name });
      // Trigger script download immediately while we have the one-shot token
      const url = `${API_BASE}/engagements/${engId}/agent-script/${data.id}?token=${encodeURIComponent(data.token)}`;
      const res = await api.get(url.replace(API_BASE, ""), { responseType: "blob" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(new Blob([res.data], { type: "text/x-python" }));
      a.download = `cyberspherex_agent_${data.id.slice(0, 8)}.py`;
      a.click();
      toast.success(`Agent "${name}" registered — token shown once: ${data.token.slice(0, 12)}…`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Registration failed");
    }
  };

  const uploadLogo = (engId) => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = "image/png,image/jpeg";
    input.onchange = async () => {
      const file = input.files?.[0];
      if (!file) return;
      if (file.size > 1024 * 1024) {
        toast.error("Logo must be < 1 MB");
        return;
      }
      const fd = new FormData();
      fd.append("file", file);
      try {
        await api.post(`/engagements/${engId}/logo`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        toast.success(`Logo uploaded (${(file.size / 1024).toFixed(1)} KB) — will appear on next PDF`);
        await refresh();
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Logo upload failed");
      }
    };
    input.click();
  };

  return (
    <div className="p-6 max-w-5xl">
      <PageHeader
        tag="Engagement / Workspaces"
        title="Engagements"
        subtitle="Each client engagement is an isolated workspace. Switch between them from the top bar; only active-engagement data is shown across modules."
        actions={
          <TButton data-testid="open-new-engagement" icon={Plus} onClick={() => setOpen(!open)}>
            New Engagement
          </TButton>
        }
      />

      {open && (
        <div className="csx-card p-5 mb-6">
          <div className="text-[10px] uppercase tracking-[0.3em] text-cyan-400 mb-3">Create Engagement</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <label className="block">
              <span className="text-[10px] uppercase tracking-widest text-slate-400">Engagement Name</span>
              <input data-testid="eng-name" className="w-full mt-1 bg-slate-950 border border-slate-800 px-3 py-2 text-sm font-mono outline-none focus:border-cyan-500/70"
                value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })}
                placeholder="Acme Bank Q1 2026 Pentest" />
            </label>
            <label className="block">
              <span className="text-[10px] uppercase tracking-widest text-slate-400">Client Name</span>
              <input data-testid="eng-client" className="w-full mt-1 bg-slate-950 border border-slate-800 px-3 py-2 text-sm font-mono outline-none focus:border-cyan-500/70"
                value={draft.client_name} onChange={(e) => setDraft({ ...draft, client_name: e.target.value })}
                placeholder="Acme Bank Ltd." />
            </label>
            <label className="block">
              <span className="text-[10px] uppercase tracking-widest text-slate-400">Engagement Type</span>
              <select data-testid="eng-type" className="w-full mt-1 bg-slate-950 border border-slate-800 px-3 py-2 text-sm"
                value={draft.engagement_type} onChange={(e) => setDraft({ ...draft, engagement_type: e.target.value })}>
                <option value="project">Project (one-off)</option>
                <option value="retainer">Retainer (monthly)</option>
                <option value="subscription">Subscription (SaaS)</option>
              </select>
            </label>
            <label className="block">
              <span className="text-[10px] uppercase tracking-widest text-slate-400">Scope Domains (comma-separated)</span>
              <input className="w-full mt-1 bg-slate-950 border border-slate-800 px-3 py-2 text-sm font-mono"
                value={draft.scope_domains} onChange={(e) => setDraft({ ...draft, scope_domains: e.target.value })}
                placeholder="acme.com, api.acme.com" />
            </label>
            <label className="block md:col-span-2">
              <span className="text-[10px] uppercase tracking-widest text-slate-400">Scope IP Ranges (comma-separated)</span>
              <input className="w-full mt-1 bg-slate-950 border border-slate-800 px-3 py-2 text-sm font-mono"
                value={draft.scope_ip_ranges} onChange={(e) => setDraft({ ...draft, scope_ip_ranges: e.target.value })}
                placeholder="10.0.0.0/24, 192.168.10.0/24" />
            </label>
            <label className="block md:col-span-2">
              <span className="text-[10px] uppercase tracking-widest text-slate-400">Notes</span>
              <textarea className="w-full mt-1 bg-slate-950 border border-slate-800 px-3 py-2 text-sm h-20"
                value={draft.notes} onChange={(e) => setDraft({ ...draft, notes: e.target.value })}
                placeholder="Authorization letter ref, SoW link, primary contact..." />
            </label>
          </div>
          <div className="mt-4 flex justify-end">
            <TButton data-testid="save-engagement" icon={busy ? Loader2 : Plus} onClick={create} disabled={busy}>
              {busy ? "Creating..." : "Create"}
            </TButton>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {engagements.map((e) => {
          const isDemo = e.id === "demo-default";
          const isActive = e.id === activeId;
          return (
            <div key={e.id} data-testid={`engagement-card-${e.id}`} className={[
              "csx-card p-4",
              isActive ? "border-cyan-500/70 shadow-[0_0_18px_rgba(6,182,212,0.2)]" : "",
            ].join(" ")}>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex items-start gap-3">
                  {e.logo_base64 && (
                    <img
                      src={`data:${e.logo_mime || "image/png"};base64,${e.logo_base64}`}
                      alt={`${e.client_name} logo`}
                      className="h-10 w-10 object-contain bg-slate-900 border border-slate-800 p-1"
                      data-testid={`logo-thumb-${e.id}`}
                    />
                  )}
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <Briefcase className="h-4 w-4 text-cyan-400" />
                      <div className="font-display text-lg text-slate-100 truncate">{e.name}</div>
                    </div>
                    <div className="text-xs text-slate-400 mt-0.5">{e.client_name}</div>
                  </div>
                </div>
                <div className="flex flex-col items-end gap-1">
                  {isDemo && <span className="text-[9px] uppercase tracking-widest px-1.5 py-0.5 border border-amber-500/60 text-amber-300 bg-amber-500/10">DEMO</span>}
                  <span className="text-[9px] uppercase tracking-widest text-slate-500">{e.engagement_type}</span>
                  <span className={`text-[9px] uppercase tracking-widest ${e.status === "archived" ? "text-slate-500" : "text-emerald-300"}`}>{e.status}</span>
                </div>
              </div>
              {e.scope?.domains?.length > 0 && (
                <div className="mt-3 text-[10px] text-slate-500">
                  domains: <span className="text-cyan-300 font-mono">{e.scope.domains.join(", ")}</span>
                </div>
              )}
              {e.scope?.ip_ranges?.length > 0 && (
                <div className="text-[10px] text-slate-500">
                  ip_ranges: <span className="text-cyan-300 font-mono">{e.scope.ip_ranges.join(", ")}</span>
                </div>
              )}
              <div className="mt-3 pt-3 border-t border-slate-800 flex items-center gap-2 flex-wrap">
                <TButton variant={isActive ? "solid" : "ghost"} onClick={() => setActiveId(e.id)} disabled={isActive}>
                  {isActive ? "Active" : "Activate"}
                </TButton>
                {!isDemo && (
                  <>
                    <TButton variant="ghost" icon={FileText} onClick={() => downloadScopeLetter(e.id)} data-testid={`scope-letter-${e.id}`}>
                      Scope Letter
                    </TButton>
                    <TButton variant="ghost" icon={Radio} onClick={() => registerAgent(e.id)} data-testid={`new-agent-${e.id}`}>
                      Agent
                    </TButton>
                    <TButton variant="ghost" icon={ImageIcon} onClick={() => uploadLogo(e.id)} data-testid={`upload-logo-${e.id}`}>
                      {e.logo_base64 ? "Replace Logo" : "Logo"}
                    </TButton>
                    <button onClick={() => archive(e.id)} className="ml-auto text-slate-500 hover:text-rose-400 p-1.5 border border-slate-800 hover:border-rose-500/60 transition-all" title="Archive engagement">
                      <Archive className="h-3.5 w-3.5" />
                    </button>
                  </>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default Engagements;
